import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Tooltip } from "@mui/joy";
import EmptyData from "../../components/EmptyData";
import { toast } from "react-toastify";
import { get_users, update_user } from "../../reduxData/user/userAction";
import CustomPagination from "../../components/CustomPagination";
import UserDetailsModal from "../../components/UserDetailsModal";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import { Box } from "@mui/joy";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "../../hooks/formatDateTime";
import { Search, SlidersHorizontal, X } from "lucide-react";

const UserManagement = () => {
    const { t, i18n } = useTranslation();
    const { allUsers, totalUsers } = useSelector((state) => state.user);
    const { isLoading } = useSelector((state) => state.loading);
    const [currentTab, setCurrentTab] = useState("seller");
    const tabs = ["seller", "buyer"];
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [approvalStatusFilter, setApprovalStatusFilter] = useState("All");
    const [restrictionTypeFilter, setRestrictionTypeFilter] = useState("All");
    const [editData, setEditData] = useState(null);
    const [resetPage, setResetPage] = useState(false);
    const { user, userDetails, userPermissions } = useSelector(state => state.auth);
    const [show, setShow] = useState(false);
    const [data, setData] = useState(null);
    const [deletedata, setDeleteData] = useState(null);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { state } = location;
    const { activeSeller } = state || {};
    const [suspendReason, setSuspendReason] = useState("");
    const [suspendReasonModal, setSuspendReasonModal] = useState(false);
    const canViewUser = userPermissions?.users?.view;
    const canSuspendUser = userPermissions?.users?.delete;
    const canShowUserActions = canViewUser || canSuspendUser;

    useEffect(() => {
        const fetchData = async (page = currentPage, rows = perPage) => {
            try {
                await get_users(dispatch, page, rows, name, statusFilter, currentTab, approvalStatusFilter, restrictionTypeFilter);
            } catch (error) {
                console.error("Error fetching user data:", error);
            }
        };

        fetchData();
    }, [currentTab, currentPage, perPage, name, email, statusFilter, approvalStatusFilter, restrictionTypeFilter]);


    useEffect(() => {
        if (activeSeller) {
            setStatusFilter('Active');
        }
    }, [activeSeller]);

    const changeDOJFormat = (data) => formatDateTime(data, i18n, false, { hour: undefined, minute: undefined });

    const handleDelete = async (item) => {
        const selectedUser = suspendReasonModal ? deletedata : item;
        const data = {
            is_deactive: selectedUser?.is_deactive ? false : true,
            ...(!selectedUser?.is_deactive && {
                suspend_reason: suspendReason,
            }),
        };
        const message = selectedUser?.is_deactive
            ? "User Active Successfully"
            : "User In-Active Successfully";
        const isEdit = await update_user(selectedUser?.id, data, navigate, dispatch, true, message);
        if (isEdit) {
            setSuspendReasonModal(false);
            setSuspendReason("");
            await get_users(dispatch, currentPage, perPage, "", statusFilter, currentTab, approvalStatusFilter, restrictionTypeFilter);
        }
    };


    const handleFilterChange = (newFilter) => {
        setStatusFilter(newFilter);
        setCurrentPage(1);
        setResetPage(true);
        get_users(dispatch, 1, perPage, name, newFilter, currentTab, approvalStatusFilter, restrictionTypeFilter).then(() => {
            setResetPage(false);
        });
    };

    const handleFilterChangeapproval = (newFilter) => {
        setApprovalStatusFilter(newFilter);
        setCurrentPage(1);
        setResetPage(true);
        get_users(dispatch, 1, perPage, name, statusFilter, currentTab, newFilter, restrictionTypeFilter).then(() => {
            setResetPage(false);
        });
    };

    const handleRestrictionFilterChange = (newFilter) => {
        setRestrictionTypeFilter(newFilter);
        setCurrentPage(1);
        setResetPage(true);
        get_users(dispatch, 1, perPage, name, statusFilter, currentTab, approvalStatusFilter, newFilter).then(() => {
            setResetPage(false);
        });
    };

    const handleuserDetail = (item) => {
        navigate(`/user/${item?.user_name}`, { state: { userDetail: item } });
    };

    const statusClassMap = {
        SELLER_REJECTED: 'cancelled',
        SELLER_REGISTERED: 'pending',
        SELLER_ACTIVE: 'approved',
        SELLER_EMAIL_VERIFIED: 'approved',
        BUYER_VERIFIED: 'approved',
        BUYER_REGISTERED: 'pending',
    };

    // const statusLabelMap = {
    //     SELLER_REJECTED: t("userManagement.approvalUnverifiedSeller"),
    //     REGISTERED: t("userManagement.approvalRegistered"),
    //     SELLER_ACTIVE: t("userManagement.approvalVerifiedSeller")
    // };

    const formatStatus = (status = "") => {
        return status
            .replace(/_/g, " ")
            .toLowerCase()
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const getActiveRestrictionType = (userData) => {
        const value = userData?.has_active_restriction ?? userData?.active_restriction ?? userData?.active_restrictions;
        if (Array.isArray(value)) {
            const activeItem = value.find((item) => String(item?.status || item?.restriction_status).toUpperCase() === "ACTIVE");
            return activeItem?.restriction_type || "";
        }
        if (value && typeof value === "object") {
            const isActive = String(value.status || value.restriction_status || "ACTIVE").toUpperCase() === "ACTIVE";
            return isActive ? value.restriction_type || "" : "";
        }
        return userData?.active_restriction_type || userData?.restriction_type || "";
    };

    const getRestrictionTypeLabel = (type) => (
        type ? t(`applyStrike.restriction.types.${type}.label`, formatStatus(type)) : t("userManagement.restrictionNone")
    );

    const restrictionTypes = currentTab === "buyer"
        ? ["ACCOUNT_SUSPENDED"]
        : ["PAYOUT_HOLD", "FINAL_SALE_BANNED", "UPLOAD_RESTRICTED", "SELLING_RESTRICTED", "ACCOUNT_UNDER_REVIEW", "ACCOUNT_SUSPENDED"];

    const clearFilters = () => {
        setName("");
        setEmail("");
        setStatusFilter("All");
        setApprovalStatusFilter("All");
        setRestrictionTypeFilter("All");
        setCurrentPage(1);
        setResetPage(true);
        window.setTimeout(() => setResetPage(false), 0);
    };

    return (
        <div>
            <div className="container user-management-page">
                <div className="user-management-header">
                    <h2>
                        {currentTab === "seller" ? t("userManagement.sellerTitle") : t("userManagement.buyerTitle")} ({totalUsers})
                    </h2>
                    <p>{t("userManagement.pageSubtitle")}</p>
                </div>

                <div className="col-lg-8 col-md-8 mt-4 mb-4">
                    <div className="text-md-end">
                        <ul className="list-inline mb-0 sorting-list">
                            {tabs.map((tab) => (
                                <li key={tab} className="list-inline-item mb-2 mb-md-0">
                                    <Link
                                        className={`text-decoration-none text-capitalize border rounded-pill px-3 py-2 ${currentTab === tab ? "active" : ""
                                            }`}
                                        onClick={() => {
                                            setCurrentTab(tab);
                                            setStatusFilter("All")
                                            setRestrictionTypeFilter("All");
                                            localStorage.setItem("itemPage", JSON.stringify(tab));
                                            setStatusFilter("All")
                                            // setRestrictionTypeFilter("All");

                                        }}
                                    >
                                        {tab == "seller" ? t("userManagement.roleSeller") : t("userManagement.roleBuyer")}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>{" "}
                </div>

                <section className="category-filter-card">
                    <div className="category-filter-heading">
                        <span className="category-filter-icon" aria-hidden="true">
                            <SlidersHorizontal size={17} />
                        </span>
                        <div>
                            <strong>{t("userManagement.findUsers")}</strong>
                            <small>{t("userManagement.filterHint")}</small>
                        </div>
                    </div>

                    <div className="category-filter-controls user-management-filter-controls">
                        <div className="category-filter-search">
                            <Search size={17} aria-hidden="true" />
                            <input
                                id="nameInput"
                                type="search"
                                aria-label={t("userManagement.searchPlaceholder")}
                                placeholder={t("userManagement.searchPlaceholder")}
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                        </div>

                        <label className="category-filter-field">
                            <span>{t("userManagement.viewByStatus")}</span>
                            {currentTab === "buyer" ? (
                                <select
                                    value={statusFilter}
                                    onChange={(e) => handleFilterChange(e.target.value)}
                                >
                                    <option value="All">{t("userManagement.statusAll")}</option>
                                    <option value="BUYER_REGISTERED">{t("userManagement.BUYER_REGISTERED")}</option>
                                    <option value="BUYER_VERIFIED">{t("userManagement.BUYER_VERIFIED")}</option>
                                </select>
                            ) : (
                                <select
                                    value={statusFilter}
                                    onChange={(e) => handleFilterChange(e.target.value)}
                                >
                                    <option value="All">{t("userManagement.statusAll")}</option>
                                    <option value="SELLER_REGISTERED">{t("userManagement.SELLER_REGISTERED")}</option>
                                    <option value="SELLER_EMAIL_VERIFIED">{t("userManagement.SELLER_EMAIL_VERIFIED")}</option>
                                    <option value="SELLER_ACTIVE">{t("userManagement.SELLER_ACTIVE")}</option>
                                    <option value="SELLER_REJECTED">{t("userManagement.SELLER_REJECTED")}</option>
                                </select>
                            )}
                        </label>

                        <label className="category-filter-field">
                            <span>{t("userManagement.viewByRestrictionType")}</span>
                            <select
                                value={restrictionTypeFilter}
                                onChange={(e) => handleRestrictionFilterChange(e.target.value)}
                            >
                                <option value="All">{t("userManagement.restrictionAll")}</option>
                                {restrictionTypes.map((type) => (
                                    <option key={type} value={type}>{getRestrictionTypeLabel(type)}</option>
                                ))}
                            </select>
                        </label>

                        <button className="category-clear-filters" type="button" onClick={clearFilters}>
                            <X size={16} aria-hidden="true" />
                            {t("userManagement.clearFilters")}
                        </button>
                    </div>
                </section>

                <div className="category-card user-management-table-card">
                  <div className="table-responsive">
                    <table className="modern-table user-management-category-table">
                        <thead>
                            <tr>
                                <th scope="col">{t("userManagement.colName")}</th>
                                <th scope="col">{t("userManagement.colEmail")}</th>
                                <th scope="col">{t("userManagement.colRole")}</th>
                                <th scope="col" className="text-center">{t("userManagement.colDateOfJoining")}</th>
                                <th scope="col" className="text-center">{t("userManagement.colStatus")}</th>
                                <th scope="col" className="text-center">{t("userManagement.colActiveRestriction")}</th>
                                {canShowUserActions &&
                                    <th scope="col" className="text-center">{t("userManagement.colAction")}</th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            {Array.isArray(allUsers) && allUsers?.length > 0 ?
                                allUsers.map((data, index) => (
                                    <tr key={index}>
                                        <td>
                                            <div className={data?.user_name ? 'w-fit-content' : ''}>
                                                {data?.user_name ? data?.user_name : 'N/A'}
                                            </div>
                                        </td>
                                        <td>{data?.email}</td>
                                        <td>{data?.role === "seller" ? t("userManagement.roleSeller") : t("userManagement.roleBuyer")}</td>
                                        <td className="text-center">{changeDOJFormat(data.created_at)}</td>

                                        <td className="text-center">
                                            <div className={`status-badge user-management-status-badge ${statusClassMap[(data?.role === "seller" ? data?.seller_status : data?.buyer_status)] || ''}`}>
                                                <span>
                                                    {formatStatus((data?.role === "seller" ? data?.seller_status : data?.buyer_status))}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <div className={`status-badge user-management-status-badge ${getActiveRestrictionType(data) ? "cancelled" : "approved"}`}>
                                                <span>{getRestrictionTypeLabel(getActiveRestrictionType(data))}</span>
                                            </div>
                                        </td>
                                        {canShowUserActions &&
                                            <td>
                                                <div className="d-flex gap-1 align-items-center text-center">
                                                    {canViewUser && (
                                                        <Tooltip title={t("userManagement.tooltipViewUser")} arrow>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary"
                                                                onClick={() => handleuserDetail(data)}>
                                                                {t("userManagement.viewButton")}
                                                            </button>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </td>}

                                    </tr>
                                )) : <EmptyData />}
                        </tbody>
                    </table>
                  </div>
                </div>

                <div className="mt-3">
                    {totalUsers > 0 && (
                        <CustomPagination
                            total={totalUsers}
                            onPageChange={(page, perPage) => {
                                setCurrentPage(page);
                                setPerPage(perPage);
                                get_users(dispatch, page, perPage, name, statusFilter, currentTab, approvalStatusFilter, restrictionTypeFilter);
                            }}
                            resetPage={resetPage}
                        />
                    )}
                </div>

                <UserDetailsModal
                    show={show}
                    handleClose={() => {
                        setData(null);
                        setShow(false);
                    }}
                    user={data}
                />

            </div>

            <Modal open={suspendReasonModal} onClose={() => setSuspendReasonModal(false)}>
                <ModalDialog>
                    <DialogTitle>User Suspend Reason</DialogTitle>
                    <DialogContent>
                        <div className="udr-modal-body">
                            <label htmlFor="udr-reason" className="udr-modal-label">
                                Reason for Suspend *
                            </label>
                            <textarea
                                id="udr-reason"
                                className="udr-modal-textarea"
                                placeholder="Please explain your reason..."
                                rows="5"
                                value={suspendReason}
                                onChange={(e) => setSuspendReason(e.target.value)}
                            />
                        </div>
                    </DialogContent>
                    <Box sx={{ p: 2 }}>
                        <div className="udr-modal-footer">
                            <button className="udr-modal-cancel" onClick={() => setSuspendReasonModal(false)}>Cancel</button>
                            <button
                                className={`udr-modal-confirm ${!suspendReason.trim() ? "udr-modal-confirm--disabled" : ""}`}
                                onClick={() => handleDelete("SELLER_REJECTED")}
                                t disabled={!suspendReason.trim()}
                            >
                                Suspend User
                            </button>
                        </div>
                    </Box>
                </ModalDialog>
            </Modal>
        </div>
    )
};

export default UserManagement;
